/* Shared block palette + renderers for the course module and the
 * qualiopi doc template builders. Keeps the main pages tight.
 */
import { useRef, useState } from "react";
import {
  Heading, Type, Image as ImageIcon, Video, Link as LinkIcon,
  List as ListIcon, Quote, AlertCircle, Minus, MoveVertical, Columns as ColumnsIcon,
  Trash2, ChevronUp, ChevronDown, Tag, Upload, Loader2,
} from "lucide-react";
import { makeBlock, BLOCK_TEMPLATES, THEMES } from "./blocks";
import { toast } from "sonner";

const BLOCK_META = {
  heading: { icon: Heading, label: "Titre" },
  paragraph: { icon: Type, label: "Paragraphe" },
  image: { icon: ImageIcon, label: "Image" },
  video: { icon: Video, label: "Video" },
  embed: { icon: LinkIcon, label: "Embed iframe" },
  list: { icon: ListIcon, label: "Liste" },
  quote: { icon: Quote, label: "Citation" },
  callout: { icon: AlertCircle, label: "Encart" },
  divider: { icon: Minus, label: "Separateur" },
  spacer: { icon: MoveVertical, label: "Espacement" },
  columns: { icon: ColumnsIcon, label: "Colonnes" },
};

export function BlockPalette({ onAdd, api }) {
  const fileInputRef = useRef(null);
  const [ocrLoading, setOcrLoading] = useState(false);

  const handleOcrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const blocks = r.data?.blocks || [];
      if (!blocks.length) { toast.error("Aucun contenu extrait"); return; }
      // Map OCR blocks to builder blocks
      for (const b of blocks) {
        if (b.type === "heading") onAdd(makeBlock("heading", { text: b.text, level: b.level || 2 }));
        else if (b.type === "list") onAdd(makeBlock("list", { items: b.items || [], ordered: !!b.ordered }));
        else if (b.type === "quote") onAdd(makeBlock("quote", { text: b.text }));
        else if (b.type === "image") onAdd(makeBlock("image", { url: b.url, caption: b.caption || "" }));
        else onAdd(makeBlock("paragraph", { text: b.text }));
      }
      toast.success(`${blocks.length} bloc(s) importe(s) depuis ${r.data.source.toUpperCase()}`);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setOcrLoading(false);
    e.target.value = "";
  };

  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl p-3" data-testid="block-palette">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Ajouter un bloc</div>
      <div className="grid grid-cols-2 gap-1.5">
        {BLOCK_TEMPLATES.map((b) => {
          const meta = BLOCK_META[b.type];
          if (!meta) return null;
          const Icon = meta.icon;
          return (
            <button
              key={b.type}
              onClick={() => onAdd(makeBlock(b.type))}
              className="flex items-center gap-2 text-xs text-slate-300 bg-[#14101c] hover:bg-[#1e1a2a] hover:text-[#f7c935] border border-white/5 hover:border-[#f7c935]/30 rounded-lg px-2.5 py-2 transition-colors"
              data-testid={"palette-" + b.type}
            >
              <Icon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{meta.label}</span>
            </button>
          );
        })}
      </div>
      {api && (
        <>
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Importer depuis un fichier</div>
            <input ref={fileInputRef} type="file" accept=".docx,.pdf,.html,.htm" onChange={handleOcrUpload} className="hidden" data-testid="ocr-file-input" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={ocrLoading}
              className="w-full flex items-center justify-center gap-2 text-xs text-[#f7c935] bg-[#f7c935]/5 hover:bg-[#f7c935]/10 border border-[#f7c935]/30 rounded-lg px-2.5 py-2 transition-colors"
              data-testid="ocr-upload-btn"
            >
              {ocrLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              <span>{ocrLoading ? "Analyse en cours..." : "DOCX / PDF / HTML → blocs"}</span>
            </button>
            <p className="text-[9px] text-slate-600 mt-1 leading-tight">Titres, listes et images sont detectes automatiquement.</p>
          </div>
        </>
      )}
    </div>
  );
}

export function TagsPanel({ tags, onPick }) {
  const [q, setQ] = useState("");
  const all = [...(tags.builtin || []), ...(tags.custom || []).map((t) => ({ ...t, custom: true }))];
  const grouped = {};
  all.forEach((t) => {
    const c = t.category || "Autre";
    if (!grouped[c]) grouped[c] = [];
    if (!q || t.key.toLowerCase().includes(q.toLowerCase()) || (t.label || "").toLowerCase().includes(q.toLowerCase())) {
      grouped[c].push(t);
    }
  });
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl p-3 max-h-[60vh] overflow-y-auto" data-testid="tags-panel">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
        <Tag className="w-3 h-3" /> Balises disponibles
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Rechercher..."
        className="w-full bg-[#14101c] border border-white/10 rounded px-2 py-1.5 text-xs text-white placeholder:text-slate-500 focus:border-[#f7c935] mb-2"
        data-testid="tags-search"
      />
      {Object.entries(grouped).map(([cat, items]) => items.length > 0 && (
        <div key={cat} className="mb-3">
          <div className="text-[9px] uppercase tracking-wider text-[#f7c935] font-bold mb-1">{cat}</div>
          <div className="flex flex-wrap gap-1">
            {items.map((t) => (
              <button
                key={t.key}
                onClick={() => onPick(t.key)}
                title={t.label + (t.example ? " — ex: " + t.example : "")}
                className={"text-[10px] px-2 py-1 rounded font-mono hover:text-[#f7c935] " + (t.custom ? "bg-purple-500/10 text-purple-300 border border-purple-500/30" : "bg-white/5 text-slate-300 border border-white/10 hover:border-[#f7c935]/40")}
                data-testid={"tag-" + t.key}
              >
                {"{{" + t.key + "}}"}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BlockControls({ onUp, onDown, onDelete, canUp, canDown }) {
  return (
    <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5 bg-[#0b0811]/90 border border-white/10 rounded-md p-0.5 z-10" data-testid="block-controls">
      <button onClick={onUp} disabled={!canUp} className="p-1 text-slate-400 hover:text-white disabled:opacity-30" title="Monter"><ChevronUp className="w-3 h-3" /></button>
      <button onClick={onDown} disabled={!canDown} className="p-1 text-slate-400 hover:text-white disabled:opacity-30" title="Descendre"><ChevronDown className="w-3 h-3" /></button>
      <button onClick={onDelete} className="p-1 text-red-400 hover:text-red-300" title="Supprimer"><Trash2 className="w-3 h-3" /></button>
    </div>
  );
}

function VideoEmbed({ block, theme }) {
  const url = block.url || "";
  let src = url;
  if (block.provider === "youtube") {
    const m = url.match(/(?:youtu\.be\/|v=)([\w-]{6,})/);
    if (m) src = "https://www.youtube.com/embed/" + m[1];
  } else if (block.provider === "vimeo") {
    const m = url.match(/vimeo\.com\/(\d+)/);
    if (m) src = "https://player.vimeo.com/video/" + m[1];
  }
  if (!url) {
    return <div className="bg-white/5 border border-dashed border-white/10 rounded-lg p-6 text-center text-sm text-slate-500">Aucune URL video</div>;
  }
  return (
    <div>
      <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
        <iframe src={src} title="video" className="absolute inset-0 w-full h-full rounded-lg" allowFullScreen />
      </div>
      {block.caption && <div className="text-xs text-center mt-2" style={{ color: theme.muted }}>{block.caption}</div>}
    </div>
  );
}

/* Render a block into editable UI */
export function EditableBlock({ block, onChange, onUp, onDown, onDelete, canUp, canDown, theme, onFocusBlock, selected }) {
  const focus = () => onFocusBlock && onFocusBlock();
  const patch = (p) => onChange({ ...block, ...p });
  const cls = "relative group rounded-lg transition-colors " + (selected ? "ring-2 ring-[#f7c935]/30" : "ring-1 ring-transparent hover:ring-white/10");

  return (
    <div className={cls} onClick={focus} data-testid={"block-" + block.type}>
      <BlockControls onUp={onUp} onDown={onDown} onDelete={onDelete} canUp={canUp} canDown={canDown} />
      <div className="p-3">
        {block.type === "heading" && (
          <input
            type="text"
            value={block.text || ""}
            onChange={(e) => patch({ text: e.target.value })}
            placeholder="Titre..."
            className="w-full bg-transparent border-0 outline-none font-black text-3xl"
            style={{ color: theme.heading, textAlign: block.align || "left" }}
          />
        )}
        {block.type === "paragraph" && (
          <textarea
            value={block.text || ""}
            onChange={(e) => patch({ text: e.target.value })}
            placeholder="Saisissez votre texte. Inserez des balises via le panneau lateral."
            rows={Math.max(2, (block.text || "").split("\n").length)}
            className="w-full bg-transparent border-0 outline-none resize-none leading-relaxed"
            style={{ color: theme.text, textAlign: block.align || "left" }}
          />
        )}
        {block.type === "image" && (
          <div>
            <input
              type="text"
              value={block.url || ""}
              onChange={(e) => patch({ url: e.target.value })}
              placeholder="URL de l'image (https://...)"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs mb-2"
              style={{ color: theme.text }}
            />
            {block.url && <img src={block.url} alt={block.caption || ""} className="rounded-lg max-h-96 mx-auto" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <input
              type="text"
              value={block.caption || ""}
              onChange={(e) => patch({ caption: e.target.value })}
              placeholder="Legende (optionnel)"
              className="w-full bg-transparent border-0 outline-none text-xs mt-2 text-center"
              style={{ color: theme.muted }}
            />
          </div>
        )}
        {block.type === "video" && (
          <div>
            <div className="flex gap-2 mb-2">
              <select value={block.provider || "youtube"} onChange={(e) => patch({ provider: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs" style={{ color: theme.text }}>
                <option value="youtube">YouTube</option>
                <option value="vimeo">Vimeo</option>
                <option value="direct">URL directe</option>
              </select>
              <input type="text" value={block.url || ""} onChange={(e) => patch({ url: e.target.value })} placeholder="URL de la video" className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" style={{ color: theme.text }} />
            </div>
            <VideoEmbed block={block} theme={theme} />
            <input type="text" value={block.caption || ""} onChange={(e) => patch({ caption: e.target.value })} placeholder="Legende (optionnel)" className="w-full bg-transparent border-0 outline-none text-xs mt-2 text-center" style={{ color: theme.muted }} />
          </div>
        )}
        {block.type === "embed" && (
          <div>
            <input type="text" value={block.url || ""} onChange={(e) => patch({ url: e.target.value })} placeholder="URL iframe (https://...)" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs mb-2" style={{ color: theme.text }} />
            {block.url && (
              <div className="relative w-full" style={{ paddingTop: "60%" }}>
                <iframe src={block.url} title="embed" className="absolute inset-0 w-full h-full rounded-lg" />
              </div>
            )}
          </div>
        )}
        {block.type === "list" && (
          <div>
            <div className="flex gap-2 mb-2">
              <label className="flex items-center gap-1 text-xs" style={{ color: theme.muted }}>
                <input type="checkbox" checked={!!block.ordered} onChange={(e) => patch({ ordered: e.target.checked })} /> Liste ordonnee
              </label>
            </div>
            {(block.items || []).map((it, i) => (
              <div key={i} className="flex gap-2 mb-1">
                <span style={{ color: theme.accent }}>{block.ordered ? i + 1 + "." : "•"}</span>
                <input type="text" value={it} onChange={(e) => { const items = [...(block.items || [])]; items[i] = e.target.value; patch({ items }); }} className="flex-1 bg-transparent border-0 outline-none text-sm" style={{ color: theme.text }} />
                <button onClick={() => patch({ items: (block.items || []).filter((_, j) => j !== i) })} className="text-red-400 text-xs">×</button>
              </div>
            ))}
            <button onClick={() => patch({ items: [...(block.items || []), "Nouvel element"] })} className="text-xs hover:underline" style={{ color: theme.accent }}>+ Ajouter un element</button>
          </div>
        )}
        {block.type === "quote" && (
          <div className="pl-4 border-l-4" style={{ borderColor: theme.accent }}>
            <textarea value={block.text || ""} onChange={(e) => patch({ text: e.target.value })} className="w-full bg-transparent border-0 outline-none italic text-lg resize-none" rows={2} style={{ color: theme.text }} />
            <input type="text" value={block.author || ""} onChange={(e) => patch({ author: e.target.value })} placeholder="— Auteur" className="w-full bg-transparent border-0 outline-none text-xs mt-1" style={{ color: theme.muted }} />
          </div>
        )}
        {block.type === "callout" && (
          <div className="border rounded-lg p-3" style={{ borderColor: theme.accent, background: theme.accent + "15" }}>
            <div className="flex gap-2 mb-1">
              <select value={block.kind || "info"} onChange={(e) => patch({ kind: e.target.value })} className="bg-transparent text-xs" style={{ color: theme.muted }}>
                <option value="info">Info</option>
                <option value="warning">Attention</option>
                <option value="success">Succes</option>
                <option value="danger">Danger</option>
              </select>
            </div>
            <textarea value={block.text || ""} onChange={(e) => patch({ text: e.target.value })} className="w-full bg-transparent border-0 outline-none resize-none text-sm" rows={2} style={{ color: theme.text }} />
          </div>
        )}
        {block.type === "divider" && <hr style={{ borderColor: theme.muted, opacity: 0.3 }} />}
        {block.type === "spacer" && (
          <div className="flex items-center gap-2">
            <input type="range" min={10} max={120} value={block.size || 20} onChange={(e) => patch({ size: parseInt(e.target.value) })} className="flex-1" />
            <span className="text-xs" style={{ color: theme.muted }}>{block.size || 20}px</span>
          </div>
        )}
        {block.type === "columns" && (
          <div>
            <div className="text-[10px] mb-1" style={{ color: theme.muted }}>Colonnes — ratio&nbsp;
              <select value={block.ratio || "1-1"} onChange={(e) => patch({ ratio: e.target.value })} className="bg-transparent text-xs" style={{ color: theme.muted }}>
                <option value="1-1">50 / 50</option>
                <option value="1-2">33 / 66</option>
                <option value="2-1">66 / 33</option>
              </select>
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: block.ratio === "1-2" ? "1fr 2fr" : block.ratio === "2-1" ? "2fr 1fr" : "1fr 1fr" }}>
              {(block.cols || [[], []]).map((col, ci) => (
                <div key={ci} className="bg-white/3 border border-dashed border-white/10 rounded p-2 min-h-[60px] text-xs" style={{ color: theme.muted }}>
                  {col.length === 0 ? <span className="italic">(colonne {ci + 1} — imbrications a venir)</span> : col.length + " blocs"}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* READ-ONLY RENDER (used for preview) */
export function BlockRenderer({ block, theme }) {
  if (block.type === "heading") {
    const level = Math.min(4, Math.max(1, block.level || 2));
    const size = { 1: "text-4xl", 2: "text-3xl", 3: "text-2xl", 4: "text-xl" }[level];
    return <div className={"font-black " + size} style={{ color: theme.heading, textAlign: block.align || "left" }}>{block.text || block.rendered || ""}</div>;
  }
  if (block.type === "paragraph") return <div className="leading-relaxed whitespace-pre-wrap" style={{ color: theme.text, textAlign: block.align || "left" }}>{block.text || block.rendered || ""}</div>;
  if (block.type === "image") return block.url ? <figure><img src={block.url} alt={block.caption || ""} className="rounded-lg max-h-96 mx-auto" />{block.caption && <figcaption className="text-xs text-center mt-2" style={{ color: theme.muted }}>{block.caption}</figcaption>}</figure> : null;
  if (block.type === "video") return <VideoEmbed block={block} theme={theme} />;
  if (block.type === "embed") return block.url ? <div className="relative w-full" style={{ paddingTop: "60%" }}><iframe src={block.url} title="embed" className="absolute inset-0 w-full h-full rounded-lg" /></div> : null;
  if (block.type === "list") {
    const Tag = block.ordered ? "ol" : "ul";
    const listCls = block.ordered ? "list-decimal pl-6" : "list-disc pl-6";
    return <Tag className={listCls} style={{ color: theme.text }}>{(block.items || []).map((it, i) => <li key={i}>{it}</li>)}</Tag>;
  }
  if (block.type === "quote") return <blockquote className="pl-4 border-l-4 italic text-lg" style={{ borderColor: theme.accent, color: theme.text }}>"{block.text || block.rendered || ""}"{block.author && <div className="text-xs mt-1 not-italic" style={{ color: theme.muted }}>— {block.author}</div>}</blockquote>;
  if (block.type === "callout") return <div className="border rounded-lg p-3 text-sm" style={{ borderColor: theme.accent, background: theme.accent + "15", color: theme.text }}>{block.text || block.rendered || ""}</div>;
  if (block.type === "divider") return <hr style={{ borderColor: theme.muted, opacity: 0.3 }} />;
  if (block.type === "spacer") return <div style={{ height: (block.size || 20) + "px" }} />;
  return null;
}

export { THEMES };
